import { useTranslation } from 'react-i18next';

import {
  apexSharesShortFormat,
  thousandFormatNumbroCurrency,
} from '@fintronners/react-utils/src/numberUtilsTSX';

import { WebBodyText14 } from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import { OrderType, QuantityType } from '@fintronners/internal-core/src/trade/constants';
import { InfoIcon } from '@/assets/icons';
import Image from 'next/image';
import Skeleton from '@/components/common/Skeleton/Skeleton';
import { usePreviewTradeFeeStore } from '@/stores/trades/preview-trade-fee';
import useGrowthBookFeature from '@fintronners/react-config/src/hooks/useGrowthBookFeature';
import { FeatureFlags } from '@fintronners/react-config/src/Config/growthbook';
import type { TradeBreakdown as TradeBreakdownType } from '@/hooks/trade/useTradeBreakdown';

const SmallTextSkeleton = () => <Skeleton width={60} height={14} />;
const LargeTextSkeleton = () => <Skeleton width={100} height={14} />;

type TradeBreakdownProps = {
  /**
   * The type of order
   */
  orderType?: OrderType;
  /**
   * The trade breakdown data containing all calculated values
   */
  tradeBreakdown: TradeBreakdownType;
  /**
   * The function to set the show available at stop loss price
   */
  setShowAvailableAtStopLossPrice?: (show: boolean) => void;
  /**
   * Whether the trade is for a mutual fund
   */
  isMutualFund?: boolean;
};

/**
 * Used to display the breakdown of a trade in the first step of the trade flow.
 */
export const TradeBreakdown = ({
  orderType,
  tradeBreakdown,
  setShowAvailableAtStopLossPrice,
  isMutualFund,
}: TradeBreakdownProps) => {
  const { t } = useTranslation();

  const { loading: isFeePreviewLoading } = usePreviewTradeFeeStore();
  const isFeesAndCommissionsEnabled = useGrowthBookFeature(
    FeatureFlags.isFeesAndCommissionsEnabled,
  );

  const {
    dollarBalance,
    shareBalance,
    shareBalanceInDollars,
    shareBalanceInDollarsAtLimitPrice,
    quantityType,
    shareAmount,
    grossOrderAmount,
    netOrderAmount,
    feeAmount,
  } = tradeBreakdown;

  const isStopLoss = orderType === OrderType.STOP_LOSS;
  const isMarketSell = orderType === OrderType.MARKET_SELL;

  return (
    <section className="bg-grey7 rounded-[10px] p-2 space-y-1">
      <div className="flex justify-between">
        {isStopLoss && (
          <>
            <div className="flex gap-1 items-center">
              <Image
                alt="Info icon"
                className="cursor-pointer"
                width={14}
                height={14}
                src={InfoIcon.src}
                onClick={() =>
                  setShowAvailableAtStopLossPrice && setShowAvailableAtStopLossPrice(true)
                }
              />
              <WebBodyText14.Regular className="text-grey72 font-normal">
                {t('miscScreens.tradeOptions.stopLossPreviewAvailableAtStopLossPrice')}
              </WebBodyText14.Regular>
            </div>

            <WebBodyText14.Regular className="text-darkestBlue font-bold">
              {thousandFormatNumbroCurrency(shareBalanceInDollarsAtLimitPrice)}
            </WebBodyText14.Regular>
          </>
        )}

        {isMarketSell && (
          <>
            <WebBodyText14.Regular className="text-grey72 font-normal">
              {t('common.availableHoldingsInDollars', { context: 'dataList' })}
            </WebBodyText14.Regular>

            <WebBodyText14.Regular className="text-darkestBlue font-bold">
              {thousandFormatNumbroCurrency(shareBalanceInDollars)}
            </WebBodyText14.Regular>
          </>
        )}

        {!isStopLoss && !isMarketSell && (
          <>
            <WebBodyText14.Regular className="text-grey72 font-normal">
              {t('generalInvestments.stockScreen.availableCash')}
            </WebBodyText14.Regular>

            <WebBodyText14.Regular className="text-darkestBlue font-bold">
              {thousandFormatNumbroCurrency(dollarBalance)}
            </WebBodyText14.Regular>
          </>
        )}
      </div>

      {(isStopLoss || isMarketSell) && (
        <div className="flex justify-between">
          <WebBodyText14.Regular className="text-grey72 font-normal">
            {t('common.availableHoldingsInShares', { context: 'dataList' })}
          </WebBodyText14.Regular>

          <WebBodyText14.Regular className="text-darkestBlue font-bold">
            {apexSharesShortFormat(shareBalance)}
          </WebBodyText14.Regular>
        </div>
      )}
      {!isMutualFund && (
        <>
          {quantityType === QuantityType.DOLLARS ? (
            <>
              <div className="flex justify-between">
                <WebBodyText14.Regular className="text-grey72 font-normal">
                  {t('common.estimatedShareAmount', { context: 'dataList' })}
                </WebBodyText14.Regular>

                <WebBodyText14.Regular className="text-darkestBlue font-bold">
                  {grossOrderAmount === 0 ? '-' : apexSharesShortFormat(shareAmount)}
                </WebBodyText14.Regular>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between">
                <WebBodyText14.Regular className="text-grey72 font-normal">
                  {t('common.estimatedDollarAmount', { context: 'dataList' })}
                </WebBodyText14.Regular>

                <WebBodyText14.Regular className="text-darkestBlue font-bold">
                  {grossOrderAmount === 0 ? '-' : thousandFormatNumbroCurrency(grossOrderAmount)}
                </WebBodyText14.Regular>
              </div>
            </>
          )}
        </>
      )}
      {isFeesAndCommissionsEnabled && (
        <div className="flex justify-between">
          <WebBodyText14.Regular className="text-grey72 font-normal">
            {t('common.transactionFee', { context: 'dataList' })}
          </WebBodyText14.Regular>

          <WebBodyText14.Regular Component="div" className="text-darkestBlue font-bold">
            {isFeePreviewLoading ? (
              <SmallTextSkeleton />
            ) : grossOrderAmount === 0 ? (
              '-'
            ) : feeAmount === 0 ? (
              t('miscScreens.tradeDisplay.tradeOrderTransactionFeeNoFee')
            ) : (
              thousandFormatNumbroCurrency(feeAmount)
            )}
          </WebBodyText14.Regular>
        </div>
      )}
      <div className="h-2" />
      <div className="flex justify-between">
        <WebBodyText14.Bold className="text-darkestBlue">
          {t('common.estimatedOrderTotal', { context: 'dataList' })}
        </WebBodyText14.Bold>

        <WebBodyText14.Bold Component="div" className="text-darkestBlue">
          {isFeePreviewLoading ? (
            <LargeTextSkeleton />
          ) : grossOrderAmount === 0 ? (
            '-'
          ) : (
            thousandFormatNumbroCurrency(netOrderAmount)
          )}
        </WebBodyText14.Bold>
      </div>
    </section>
  );
};
